import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { Users, DollarSign, ShieldCheck, Clock, BookOpen, UtensilsCrossed, Building2, MapPin, Bike, CheckCircle, AlertCircle, School, CreditCard, XCircle } from 'lucide-react';
import { useSelector } from 'react-redux';
import AdminHeader from '../components/common/AdminHeader';
import { useBranchInfo } from '../hooks/useBranchInfo';
import { getPickupsSummary } from '../services/deliveryAgent';
import { getSubscriptionSummary } from '../services/subscriptionData';
import { getZonePickups, getZoneSubscriptionData } from '../services/zoneManager';
import type { RootState } from '../lib/store';

const CARDS = [
  { label: 'Employees',         desc: 'Manage employees',                    icon: Users,           path: '/user-management',    screen: 'employees',             color: 'bg-brand-50 border-brand-200 text-brand-500'    },
  { label: 'Verification',      desc: 'Review pending child verifications',  icon: ShieldCheck,     path: '/verification',       screen: 'verified child',        color: 'bg-green-50 border-green-200 text-green-600'    },
  { label: 'Pricing',           desc: 'Master prices & subscription plans',  icon: DollarSign,      path: '/pricing-management', screen: 'masterprices',          color: 'bg-amber-50 border-amber-200 text-amber-600'    },
  { label: 'History',           desc: 'Price & subscription change history', icon: Clock,           path: '/history',            screen: 'masterpriceshistory',   color: 'bg-purple-50 border-purple-200 text-purple-600' },
  { label: 'Masters',           desc: 'States, cities, schools & more',      icon: BookOpen,        path: '/masters',            screen: 'city-state-master',     color: 'bg-brand-50 border-brand-200 text-brand-500'    },
  { label: 'Lunch Updates',     desc: 'Send and view lunch status updates',  icon: UtensilsCrossed, path: '/lunch-updates',      screen: '',                      color: 'bg-orange-50 border-orange-200 text-orange-600' },
  { label: 'Pickups',           desc: "Today's delivery pickups & summary",  icon: Bike,            path: '/pickups',            screen: 'pickups',               color: 'bg-violet-50 border-violet-200 text-violet-600' },
  { label: 'Subscription Data', desc: 'View active, expiring & inactive',    icon: CreditCard,      path: '/subscription-data',  screen: 'subscription data',     color: 'bg-teal-50 border-teal-200 text-teal-600'       },
];

interface PickupSummary {
  Total_Deliveries: number;
  Completed_Deliveries: number;
  InTransit_Deliveries: number;
  Pending_Deliveries: number;
  Deliveries_Per_School: Record<string, number>;
}

interface SubscriptionSummary {
  Total_Subscriptions: number;
  Active_Subscriptions: number;
  Inactive_Subscriptions: number;
  Expiring_in_10_days: number;
  Expired_Subscriptions: number;
  Ready_For_Verification: number;
}

export default function BranchInchargeDashboard() {
  const navigate = useNavigate();
  const { branchInfo } = useBranchInfo();
  const { loaded: permsLoaded, data: permissions } = useSelector((state: RootState) => state.permissions);

  const canView = (screenKey: string) =>
    !screenKey || !permsLoaded || (screenKey in permissions && permissions[screenKey]?.Can_View !== false);

  const [pickupSummary, setPickupSummary] = useState<PickupSummary | null>(null);
  const [subSummary, setSubSummary]       = useState<SubscriptionSummary | null>(null);

  const load = useCallback(async () => {
    if (!branchInfo) return;
    try {
      if (canView('pickups')) {
        const res = branchInfo.Zone_Code
          ? await getZonePickups(branchInfo.Zone_Code, true)
          : await getPickupsSummary(branchInfo.Branch_Code);
        setPickupSummary((res.data as any)?.Summary ?? null);
      }
      if (canView('subscription data')) {
        const res = branchInfo.Zone_Code
          ? await getZoneSubscriptionData(branchInfo.Zone_Code, true)
          : await getSubscriptionSummary(branchInfo.Branch_Code);
        setSubSummary((res.data as any)?.Summary ?? null);
      }
    } catch { /* silent */ }
  }, [branchInfo]);

  useEffect(() => { load(); }, [load]);

  const isZone = !!branchInfo?.Zone_Code;
  const visibleCards = CARDS.filter(c => canView(c.screen));

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />
      <main className="max-w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            {isZone
              ? <MapPin className="w-6 h-6 text-brand-500" />
              : <Building2 className="w-6 h-6 text-brand-500" />}
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
              {isZone ? 'Zone Dashboard' : 'Branch Dashboard'}
            </h1>
          </div>
          {branchInfo && (
            <p className="text-gray-500 text-sm">
              {branchInfo.Branch_Name} &middot; {branchInfo.City}, {branchInfo.State}
            </p>
          )}
        </div>

        {/* Pickups widget */}
        {canView('pickups') && pickupSummary && (
          <div
            className="bg-white rounded-2xl border border-violet-100 shadow-sm overflow-hidden mb-8 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/pickups')}
          >
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-violet-50/60">
              <div className="flex items-center gap-2">
                <Bike className="w-4 h-4 text-violet-600" />
                <p className="font-bold text-gray-800 text-sm">Today's Pickups</p>
              </div>
              <span className="text-xs text-violet-600 font-semibold">View details →</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-100">
              {[
                { label: 'Total',      value: pickupSummary.Total_Deliveries,    icon: <Bike className="w-3.5 h-3.5" />,        color: 'text-violet-700' },
                { label: 'Completed',  value: pickupSummary.Completed_Deliveries, icon: <CheckCircle className="w-3.5 h-3.5" />, color: 'text-green-600'  },
                { label: 'In Transit', value: pickupSummary.InTransit_Deliveries, icon: <Bike className="w-3.5 h-3.5" />,        color: 'text-amber-600'  },
                { label: 'Pending',    value: pickupSummary.Pending_Deliveries,   icon: <AlertCircle className="w-3.5 h-3.5" />, color: 'text-red-500'    },
              ].map((s) => (
                <div key={s.label} className="bg-white px-4 py-3 text-center">
                  <p className={`text-2xl font-extrabold ${s.color}`}>{s.value}</p>
                  <div className={`flex items-center justify-center gap-1 mt-0.5 ${s.color}`}>
                    {s.icon}
                    <p className="text-xs font-medium text-gray-500">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
            {Object.keys(pickupSummary.Deliveries_Per_School).length > 0 && (
              <div className="px-5 py-3 border-t border-gray-100">
                <div className="flex items-center gap-1.5 mb-2">
                  <School className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-xs font-semibold text-gray-500">Per School</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(pickupSummary.Deliveries_Per_School).map(([school, count]) => (
                    <span key={school} className="text-xs bg-brand-50 text-brand-700 border border-brand-100 font-semibold px-2.5 py-1 rounded-full">
                      {school}: {count}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Subscription widget */}
        {canView('subscription data') && subSummary && (
          <div
            className="bg-white rounded-2xl border border-teal-100 shadow-sm overflow-hidden mb-8 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/subscription-data')}
          >
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-teal-50/60">
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-teal-600" />
                <p className="font-bold text-gray-800 text-sm">Subscription Overview</p>
              </div>
              <span className="text-xs text-teal-600 font-semibold">View details →</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-gray-100">
              {[
                { label: 'Total',           value: subSummary.Total_Subscriptions,    icon: <CreditCard className="w-3.5 h-3.5" />,  color: 'text-gray-700'  },
                { label: 'Active',          value: subSummary.Active_Subscriptions,   icon: <CheckCircle className="w-3.5 h-3.5" />, color: 'text-green-600' },
                { label: 'Inactive',        value: subSummary.Inactive_Subscriptions, icon: <XCircle className="w-3.5 h-3.5" />,    color: 'text-gray-400'  },
                { label: 'Expiring Soon',   value: subSummary.Expiring_in_10_days,    icon: <Clock className="w-3.5 h-3.5" />,      color: 'text-amber-600' },
                { label: 'Expired',         value: subSummary.Expired_Subscriptions,  icon: <AlertCircle className="w-3.5 h-3.5" />,color: 'text-red-500'   },
                { label: 'Ready to Verify', value: subSummary.Ready_For_Verification, icon: <CheckCircle className="w-3.5 h-3.5" />,color: 'text-brand-500' },
              ].map((s) => (
                <div key={s.label} className="bg-white px-4 py-3 text-center">
                  <p className={`text-2xl font-extrabold ${s.color}`}>{s.value}</p>
                  <div className={`flex items-center justify-center gap-1 mt-0.5 ${s.color}`}>
                    {s.icon}
                    <p className="text-xs font-medium text-gray-500">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick-nav cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {visibleCards.map(({ label, desc, icon: Icon, path, color }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              className="flex items-start gap-4 p-5 rounded-2xl border bg-white hover:shadow-md transition-all text-left hover:scale-[1.01] active:scale-[0.99]"
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 border ${color}`}>
                <Icon className="w-6 h-6" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-base">{label}</p>
                <p className="text-gray-500 text-sm mt-0.5">{desc}</p>
              </div>
            </button>
          ))}
        </div>
      </main>
    </div>
  );
}
